import React, { useContext, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { CssBaseline, ThemeProvider } from '@mui/material'
import reportWebVitals from './reportWebVitals'
import { AppUtils, AuthenticatedAppProvider, EbAuthenticatedAppContext, VoidLogger } from '@eidsivabredband/app-framework'
import type { IAuthenticationConfig } from '@eidsivabredband/app-framework'
import './index.css'
import App from './App'
import { darkTheme, lightTheme } from './assets/theme'
import { AppSettingsContextProvider, useAppContext } from './context/AppSettingsContextProvider'
import { SnackbarProvider } from 'notistack'
import SnackbarCloseButton from './components/Buttons/SnackbarCloseButton'
import EventsProvider from './views/Events/EventsProvider'
import RestrictedAccess from './components/feedback/RestrictedAccess'
import UserContextProvider from './context/UserContextProvider'

const container = document.getElementById('root')
const root = createRoot(container!)

const AppWrapper = () => {
  const { isDarkTheme } = useAppContext()
  const { context } = useContext(EbAuthenticatedAppContext)
  const [isEmployee, setIsEmployee] = React.useState<boolean>(false)
  const [isRestricted, setIsRestricted] = React.useState<boolean>(false)

  useEffect(() => {
    const checkValidRole = async () => {
      const response = await context.restApiClient.buildRequest(`/api/checkuser`).getAsync()
      if (response.response?.status === 200) {
        setIsEmployee(true)
      } else {
        setIsRestricted(true)
      }
    }

    checkValidRole()
  }, [])

  return (
    <ThemeProvider theme={isDarkTheme ? darkTheme : lightTheme}>
      <CssBaseline />
      {isRestricted && <RestrictedAccess errorCode={403} />}

      {isEmployee && (
        <SnackbarProvider
          maxSnack={12}
          style={{ borderRadius: '8px' }}
          autoHideDuration={10000}
          action={(snackbarKey) => <SnackbarCloseButton snackbarKey={snackbarKey} />}
        >
          <EventsProvider>
            <App />
          </EventsProvider>
        </SnackbarProvider>
      )}
    </ThemeProvider>
  )
}

AppUtils.renderApp('/api/auth-config', (config: IAuthenticationConfig) => {
  root.render(
    <React.StrictMode>
      <AuthenticatedAppProvider authConfig={config} logger={new VoidLogger()}>
        <AppSettingsContextProvider>
          <UserContextProvider>
            <BrowserRouter>
              <AppWrapper />
            </BrowserRouter>
          </UserContextProvider>
        </AppSettingsContextProvider>
      </AuthenticatedAppProvider>
    </React.StrictMode>,
  )
})

reportWebVitals()
